'use client';

import { useFeatureFlag } from '@/@feature-flags';
import { useSubsidiaryFilterOptional } from '@/@subsidiary/context';
import { useAccountViewAsTarget } from '@/@subsidiary/hooks/useAccountViewAsTarget';
import { VIEW_AS_FLAG, VIEW_AS_REPORTS_FLAG } from '@/lib/view-as/constants';
import { useSession } from '@/providers/enhanced-session';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useRouter, useSearchParams } from 'next/navigation';

interface URLSyncState {
    conversationId?: string;
    isSharedParam: boolean;
    reportId?: string;
}

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { AgentModes, API_ENDPOINTS, DispatchType, QUERY_CONSTANTS, QUERY_KEYS } from './constants';
import { useChatReducer } from './hooks/useChatReducer';
import { useChatStreaming } from './hooks/useChatStreaming';
import { useConversationLifecycle } from './hooks/useConversationLifecycle';
import { useHistoryOperations } from './hooks/useHistoryOperations';
import { useReportKickoff } from './hooks/useReportKickoff';
import { noteConversationStart } from './utils/first-data-answer';
import { useSeedTitle } from './hooks/useSeedTitle';
import type { StudioAIProviderProps } from './props';
import { resolveConversationOwnership } from './utils/conversation-ownership';
import { readStoredCreditGate, writeStoredCreditGate } from './utils/credit-gate-storage';
import {
    conversationViewAsMismatch,
    deriveMasqueradeClient,
    hasStaffViewAsControl,
    isPlannerAllowedForClient,
    isPlannerLockedForClient,
    mismatchLockedReason,
    resolveEffectiveAgentMode,
    VIEW_AS_REPORTS_UNAVAILABLE,
    type ViewAsConversationMismatch,
    type ViewAsConversationTag,
    type ViewAsSendGuardState,
} from './utils/view-as-dani';
import { studioAIService } from './services';
import type {
    AgentMode,
    AgentOptionsPayload,
    ChatMessage,
    ChatState,
    CreditGateData,
    FreemiumLimitData,
    PlannerFormState,
    StudioAIContextValue,
    StudioAIConversation,
} from './types';

const StudioAIContext = createContext<StudioAIContextValue | undefined>(undefined);

/**
 * DANI's staff View-as state (M4S-5670, docs/pdlc/cs-view-as-client.md §4).
 * Its own context so `StudioAIContextValue` and every caller that mocks
 * `useStudioAI` stay as they are; outside a provider it reads as "not
 * viewing as anyone, nothing locked".
 */
export interface StudioAIViewAsValue {
    /** The open conversation was run as another client, or as the staff member. */
    conversationMismatch: ViewAsConversationMismatch | null;
    /** Why the composer is locked, shown in place of its placeholder; null when it is not. */
    inputLockedReason: string | null;
    /** `isPlannerAllowedForClient` for this user: plan mode may run as a client. */
    plannerAllowedForClient: boolean;
    /** Staff with the View-as control (`studio-view-as`), who never use the DANI-only picker. */
    hasViewAsControl: boolean;
}

const NO_VIEW_AS: StudioAIViewAsValue = Object.freeze({
    conversationMismatch: null,
    inputLockedReason: null,
    plannerAllowedForClient: false,
    hasViewAsControl: false,
});

const StudioAIViewAsContext = createContext<StudioAIViewAsValue>(NO_VIEW_AS);

export function useStudioAIViewAs(): StudioAIViewAsValue {
    return useContext(StudioAIViewAsContext);
}

export function StudioAIProvider({ children, disableNavigation = false }: StudioAIProviderProps) {
    const [state, dispatch] = useChatReducer();
    const queryClient = useQueryClient();
    const { data: session } = useSession();
    const router = useRouter();

    const canNavigate = useRef(!disableNavigation);
    canNavigate.current = !disableNavigation;

    const messagesRef = useRef(state.messages);
    messagesRef.current = state.messages;

    const [urlSync, setUrlSync] = useState<URLSyncState>({ isSharedParam: false });

    // ── Conversation tracking (outside reducer) ─────────────────────────
    // For dani pages: URL is the source of truth, this state mirrors it.
    // For persistent assistant: this state IS the source of truth.
    const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
    const currentConversationIdRef = useRef<string | null>(currentConversationId);
    currentConversationIdRef.current = currentConversationId;

    // Determines whether the current conversation is owned by the user.
    // Two inputs, resolved by `resolveConversationOwnership`: what the API said
    // (keyed by conversation, absent = never asked) and which conversations this
    // client started. The API's answer vetoes the optimistic claim. See BT-2010.
    const [serverOwnerById, setServerOwnerById] = useState<Record<string, boolean>>({});
    const [createdConversationIds, setCreatedConversationIds] = useState<ReadonlySet<string>>(
        () => new Set(),
    );
    const serverOwnerByIdRef = useRef(serverOwnerById);
    serverOwnerByIdRef.current = serverOwnerById;
    const urlSyncRef = useRef(urlSync);
    urlSyncRef.current = urlSync;

    /** Record what the API said about a conversation's owner. */
    const setServerConversationOwner = useCallback((conversationId: string, owner: boolean) => {
        setServerOwnerById((prev) =>
            prev[conversationId] === owner ? prev : { ...prev, [conversationId]: owner },
        );
    }, []);

    /**
     * Claim a conversation this client just started. Called from the send path,
     * which is the one choke point every new-conversation entry point funnels
     * through, so all four Share surfaces are covered by this single call.
     */
    const claimConversationOwnership = useCallback((conversationId: string) => {
        // The API has already ruled on this conversation; do not second-guess it.
        // (`resolveConversationOwnership` would ignore the claim anyway — this
        // just avoids a pointless state write.)
        if (serverOwnerByIdRef.current[conversationId] !== undefined) return;
        // A shared link or the plan-report route deliberately skips the load in
        // the URL-sync effect below, so there is no server answer available to
        // veto a wrong claim. Stay silent rather than guess.
        if (urlSyncRef.current.isSharedParam || urlSyncRef.current.reportId) return;
        setCreatedConversationIds((prev) =>
            prev.has(conversationId) ? prev : new Set(prev).add(conversationId),
        );
    }, []);

    const isCurrentConversationOwner = useMemo(
        () =>
            resolveConversationOwnership({
                currentConversationId,
                serverOwnerById,
                createdConversationIds,
            }),
        [currentConversationId, serverOwnerById, createdConversationIds],
    );

    // Only unaffiliated freemium users hit /freemium/chat; trial-org users go through standard /chat.
    const shouldUseFreemiumChat = useMemo(
        () => session?.user?.isUnaffiliated === true,
        [session?.user?.isUnaffiliated],
    );
    const chatEndpointOverride = shouldUseFreemiumChat ? API_ENDPOINTS.FREEMIUM_CHAT : undefined;

    // Masquerade state. The DANI-only picker's client (D9) is local state; under
    // staff View-as the client DANI runs as is derived from the View-as target
    // instead (M4S-5670 §4), so opening a conversation, `reset()` or the
    // history-load error path can never change or drop it.
    const [localMasqueradeClient, setLocalMasqueradeClient] = useState<{
        id: string;
        name: string;
    } | null>(null);
    const viewAsTarget = useAccountViewAsTarget();
    const isGlobalViewAsActive = viewAsTarget != null;
    const masqueradeClient = useMemo(
        () => deriveMasqueradeClient({ viewAsTarget, localClient: localMasqueradeClient }),
        [viewAsTarget, localMasqueradeClient],
    );
    const masqueradeClientRef = useRef(masqueradeClient);
    masqueradeClientRef.current = masqueradeClient;

    // Locked only for the in-chat local client picker.
    const isMasqueradeLocked = !!masqueradeClient && state.messages.length > 0;

    // Staff View-as for DANI: who has the control, whether plan mode may run as
    // a client, and the open conversation's mismatch lock.
    const viewAsFlag = useFeatureFlag(VIEW_AS_FLAG);
    const viewAsReportsFlag = useFeatureFlag(VIEW_AS_REPORTS_FLAG);
    const plannerModeFlag = useFeatureFlag('studio-ai-planner-mode');
    const plannerAllowedForClient = isPlannerAllowedForClient({
        viewAsReportsFlag,
        plannerModeFlag,
        isFreemium: session?.user?.isUnaffiliated === true,
    });
    const hasViewAsControl = hasStaffViewAsControl({
        isInternal: session?.user?.isInternal === true,
        hasSubsidiaryAccess: session?.user?.hasSubsidiaryAccess === true,
        isUnaffiliated: session?.user?.isUnaffiliated === true,
        viewAsFlag,
    });
    // Who the open conversation was run as, and the lock that follows from it:
    // derived, so it tracks View-as and the flags as they settle.
    const [viewAsConversationTag, setViewAsConversationTag] =
        useState<ViewAsConversationTag | null>(null);
    const viewAsConversationMismatch = useMemo<ViewAsConversationMismatch | null>(
        () =>
            viewAsConversationTag
                ? conversationViewAsMismatch({
                      conversation: viewAsConversationTag,
                      isOwner: viewAsConversationTag.isOwner,
                      isGlobalViewAsActive,
                      targetSfAccountId: viewAsTarget?.sfAccountId ?? null,
                      hasViewAsControl,
                  })
                : null,
        [viewAsConversationTag, isGlobalViewAsActive, viewAsTarget?.sfAccountId, hasViewAsControl],
    );
    // A locked conversation never runs as its tag through the DANI-only picker,
    // even when the lock appears after the load (flags settling late).
    useEffect(() => {
        if (viewAsConversationMismatch) setLocalMasqueradeClient(null);
    }, [viewAsConversationMismatch]);
    // Read by the lifecycle when a conversation loads.
    const viewAsBindingRef = useRef({
        isGlobalViewAsActive,
        targetSfAccountId: viewAsTarget?.sfAccountId ?? null,
        hasViewAsControl,
    });
    viewAsBindingRef.current = {
        isGlobalViewAsActive,
        targetSfAccountId: viewAsTarget?.sfAccountId ?? null,
        hasViewAsControl,
    };

    // Agent mode & planner state
    const [agentMode, setAgentMode] = useState<AgentMode>(null);
    const effectiveAgentMode = useMemo<AgentMode>(
        () =>
            resolveEffectiveAgentMode({
                agentMode,
                masqueradeClient,
                isGlobalViewAsActive,
                plannerAllowedForClient,
            }),
        [masqueradeClient, agentMode, isGlobalViewAsActive, plannerAllowedForClient],
    );
    const [plannerFormState, setPlannerFormState] = useState<PlannerFormState | null>(null);
    const [plannerRunId, setPlannerRunId] = useState<string | null>(null);
    const [plannerPhase, setPlannerPhase] = useState<'report' | null>(null);
    const [plannerTemplateId, setPlannerTemplateId] = useState<number | null>(null);
    const [plannerReportData, setPlannerReportData] = useState<{
        status?: string;
        reportId?: number;
        reportUrl?: string;
    } | null>(null);
    const [reportPollingConversationId, setReportPollingConversationId] = useState<string | null>(
        null,
    );
    const [smartRoutingEnabled, setSmartRoutingEnabled] = useState(false);

    // Freemium limit
    const [freemiumLimitReached, setFreemiumLimitReached] = useState(false);
    const [freemiumLimitData, setFreemiumLimitData] = useState<FreemiumLimitData | null>(null);

    // Credit gate — persisted for the browser session so pause state survives new conversations
    const [creditGateData, setCreditGateDataState] = useState<CreditGateData | null>(() =>
        readStoredCreditGate(),
    );
    const setCreditGateData = useCallback((data: CreditGateData | null) => {
        setCreditGateDataState(data);
        // A personal limit is not an org-cap pause. Keeping it in session storage
        // would block the next visit after an administrator raised the limit.
        writeStoredCreditGate(data?.reason === 'user_spend_limit_exceeded' ? null : data);
    }, []);
    const clearCreditGate = useCallback(() => setCreditGateData(null), [setCreditGateData]);

    // Shared view
    const [isSharedView, setIsSharedView] = useState(false);

    // Agent interactive options
    const [pendingOptions, setPendingOptions] = useState<AgentOptionsPayload | null>(null);

    // ── Dispatch wrappers that compose into hooks ───────────────────────
    // `addMessage` is defined up here (alongside the state setters) so the
    // kickoff hook can consume it before the streaming hook composes — the
    // streaming hook's `onReportGenerationStarted` callback closes over
    // `handleReportKickedOff`. The rest of the dispatch wrappers stay grouped
    // below with the context-value plumbing.
    const addMessage = useCallback(
        (msg: ChatMessage) => dispatch({ type: DispatchType.AddMessage, payload: msg }),
        [dispatch],
    );

    // ── Composed hooks ──────────────────────────────────────────────────
    // Ref used to break the streaming↔lifecycle initialization cycle:
    // streaming is constructed before lifecycle (it owns duplicateMessagesRef),
    // so onGenerationInProgress is wired via a ref that lifecycle populates.
    const loadConversationMessagesRef = useRef<((conversationId: string) => void) | undefined>(
        undefined,
    );
    const handleGenerationInProgress = useCallback((conversationId: string) => {
        loadConversationMessagesRef.current?.(conversationId);
    }, []);

    const stopPollingRef = useRef<(() => void) | undefined>(undefined);
    const handleStopPolling = useCallback(() => {
        stopPollingRef.current?.();
    }, []);

    const reportKickoff = useReportKickoff({
        currentConversationId,
        addMessage,
        setPlannerReportData,
        setPlannerRunId,
        setPlannerPhase,
    });

    // The send guard's view of View-as: the open conversation's mismatch lock,
    // the SF ids of the client's group (so a send as an org narrowed within the
    // group is still the session's client), and whether plan mode may run as
    // the client.
    const subsidiaryFilter = useSubsidiaryFilterOptional();
    const viewAsGuardRef = useRef<ViewAsSendGuardState>({ mismatch: null, groupSfAccountIds: [] });
    viewAsGuardRef.current = {
        mismatch: viewAsConversationMismatch,
        groupSfAccountIds: isGlobalViewAsActive
            ? (subsidiaryFilter?.hierarchyOptions ?? [])
                  .map((o) => o.sfAccountId)
                  .filter((id): id is string => Boolean(id))
            : [],
        plannerSelected: agentMode === AgentModes.PLANNER,
        plannerAllowedForClient,
    };

    const streaming = useChatStreaming({
        dispatch,
        state,
        queryClient,
        masqueradeClientRef,
        viewAsGuardRef,
        currentConversationIdRef,
        chatEndpointOverride,
        shouldUseFreemiumChat,
        setCurrentConversationId,
        claimConversationOwnership,
        setPlannerRunId,
        setPlannerPhase,
        onFreemiumLimitReached: (data) => {
            setFreemiumLimitReached(true);
            setFreemiumLimitData(data);
        },
        onCreditGatePaused: (data) => {
            setCreditGateData(data);
        },
        onAgentOptions: setPendingOptions,
        onReportGenerationStarted: (runId, brandLabel) => {
            if (process.env.NODE_ENV === 'development') {
                console.debug('[DANI] report generation started, runId:', runId);
            }
            reportKickoff.handleReportKickedOff(runId, brandLabel);
        },
        onReportReady: (reportId) => {
            if (process.env.NODE_ENV === 'development') {
                console.debug('[DANI] report ready, reportId:', reportId);
            }
            setPlannerReportData({ status: 'COMPLETE', reportId });
        },
        onGenerationInProgress: handleGenerationInProgress,
        onStopPolling: handleStopPolling,
    });

    const lifecycle = useConversationLifecycle({
        dispatch,
        state,
        router,
        canNavigateRef: canNavigate,
        setCurrentConversationId,
        setServerConversationOwner,
        setMasqueradeClient: setLocalMasqueradeClient,
        viewAsBindingRef,
        setViewAsConversationTag,
        setAgentMode,
        setPlannerFormState,
        setPlannerRunId,
        setPlannerPhase,
        setPlannerTemplateId,
        setPlannerReportData,
        setIsSharedView,
        setPendingOptions,
        setSmartRoutingEnabled,
        duplicateMessagesRef: streaming.duplicateMessagesRef,
    });

    loadConversationMessagesRef.current = lifecycle.loadConversationMessages;
    stopPollingRef.current = lifecycle.stopPolling;

    // Explicit org switches bump `orgSwitchEpoch` in the subsidiary provider;
    // that alone starts a new conversation so preference/hierarchy hydration
    // cannot wipe an in-flight /dani/[id] load.
    //
    // Under staff View-as (M4S-5670 §4) DANI's client is derived from the
    // View-as target, so nothing binds it here, and the epoch does nothing:
    // start, switch and Clear reload the tab, and a start that fails must leave
    // the open conversation alone. The one in-tab change of client is narrowing
    // within the client's group, which starts a new conversation below so a
    // conversation never continues as another org.
    const orgSwitchEpoch = subsidiaryFilter?.orgSwitchEpoch ?? 0;
    const prevOrgSwitchEpochRef = useRef<number | undefined>(undefined);
    const hasActiveChatRef = useRef(false);
    hasActiveChatRef.current = state.messages.length > 0 || !!currentConversationId;
    const stopConversationRef = useRef(streaming.stopConversation);
    stopConversationRef.current = streaming.stopConversation;
    const startNewConversation = lifecycle.startNewConversation;
    const startNewConversationRef = useRef(startNewConversation);
    startNewConversationRef.current = startNewConversation;
    const isGlobalViewAsActiveRef = useRef(isGlobalViewAsActive);
    isGlobalViewAsActiveRef.current = isGlobalViewAsActive;

    // For a first-message send from the landing page — the one brand-new-
    // conversation navigation that isn't already one of this context's own
    // helpers (those all set the ref themselves before pushing).
    const markNavigatingToNewChat = useCallback(() => {
        lifecycle.isNavigatingToNewChatRef.current = true;
    }, [lifecycle.isNavigatingToNewChatRef]);

    useEffect(() => {
        if (prevOrgSwitchEpochRef.current === undefined) {
            prevOrgSwitchEpochRef.current = orgSwitchEpoch;
            return;
        }
        if (prevOrgSwitchEpochRef.current === orgSwitchEpoch) return;
        prevOrgSwitchEpochRef.current = orgSwitchEpoch;

        if (isGlobalViewAsActiveRef.current) return;
        if (!hasActiveChatRef.current) return;
        void stopConversationRef.current();
        startNewConversationRef.current();
    }, [orgSwitchEpoch]);

    // Narrowing within the View-as client's group changes the derived client:
    // start a new conversation rather than continue this one as another org.
    const viewAsClientId = isGlobalViewAsActive ? (masqueradeClient?.id ?? null) : null;
    const prevViewAsClientIdRef = useRef(viewAsClientId);
    useEffect(() => {
        const previous = prevViewAsClientIdRef.current;
        prevViewAsClientIdRef.current = viewAsClientId;
        if (!previous || !viewAsClientId || previous === viewAsClientId) return;
        if (!hasActiveChatRef.current) return;
        void stopConversationRef.current();
        startNewConversationRef.current();
    }, [viewAsClientId]);

    const history = useHistoryOperations({
        dispatch,
        messages: state.messages,
    });

    // The chat an insight card opens and the follow-up question it leaves in
    // the composer, scoped to that chat's id so neither reaches another, and
    // the name the chat keeps once DANI has saved it (docs/pdlc/insights-dive-deep.md §3.3).
    const [seededChat, setSeededChat] = useState<{ conversationId: string; draft: string } | null>(
        null,
    );
    const nameSeededChat = useSeedTitle({
        currentConversationId,
        threadId: state.currentThreadId,
        isLoading: state.isLoading,
        renameConversation: history.renameConversation,
    });
    const seedConversation = useCallback(
        (messages: ChatMessage[], { draft, title }: { draft?: string; title?: string } = {}) => {
            const conversationId = lifecycle.seedConversation(messages);
            // Phase 0 of DANI memory: a dive-deep insight starts with the finding on
            // screen; only the person's messages after it count (utils/first-data-answer.ts).
            noteConversationStart(conversationId, {
                entry: 'insight',
                kind: 'chat',
                startIndex: messages.length,
            });
            setSeededChat({ conversationId, draft: draft?.trim() ?? '' });
            nameSeededChat(conversationId, title);
        },
        [lifecycle, nameSeededChat],
    );

    // ── Conversation-level report polling ───────────────────────────────
    // Fallback for environments (e.g. test1) where DANI doesn't include a
    // run_id on thread_metadata events. Polls the conversation record every
    // 20s. Two exit paths:
    //   1. conv.runId appears  → hand off to useActivePlannerReports which
    //      polls /plan-mode/status/:runId and gives intermediate statuses.
    //   2. conv.reportStatus==='COMPLETE' → set plannerReportData directly.
    //
    // Use a ref so the interval callback always calls the latest version of
    // handleReportKickedOff without needing it in the effect deps array.
    const handleReportKickedOffRef = useRef(reportKickoff.handleReportKickedOff);
    handleReportKickedOffRef.current = reportKickoff.handleReportKickedOff;

    useEffect(() => {
        if (!reportPollingConversationId) return;
        const intervalId = setInterval(async () => {
            try {
                const conv = await studioAIService.getConversationHistory(
                    reportPollingConversationId,
                );
                if (process.env.NODE_ENV === 'development') {
                    console.log('[DANI poll]', {
                        runId: conv.runId,
                        reportStatus: conv.reportStatus,
                        reportId: conv.reportId,
                    });
                }
                // Path 1: backend set runId on the conversation — hand off to
                // the standard plan-mode/status/:runId polling mechanism.
                if (conv.runId) {
                    handleReportKickedOffRef.current(conv.runId);
                    setReportPollingConversationId(null);
                    return;
                }
                // Path 2: reportStatus set directly without a runId.
                if (conv.reportStatus === 'COMPLETE' && conv.reportId != null) {
                    setPlannerReportData({
                        status: 'COMPLETE',
                        reportId: conv.reportId,
                        reportUrl: conv.reportUrl,
                    });
                    setReportPollingConversationId(null);
                }
            } catch {
                // Ignore transient errors; keep polling
            }
        }, 20_000);
        return () => clearInterval(intervalId);
    }, [reportPollingConversationId]);

    const startReportPolling = useCallback(() => {
        const convId = currentConversationIdRef.current;
        if (!convId) return;
        if (process.env.NODE_ENV === 'development') {
            console.log('[DANI poll] starting conversation poll for', convId);
        }
        setReportPollingConversationId(convId);
    }, []);

    // ── Standalone queries ──────────────────────────────────────────────

    const activeConfigQuery = useQuery({
        queryKey: [...QUERY_KEYS.ACTIVE_CONFIG, shouldUseFreemiumChat],
        queryFn: () => studioAIService.getActiveConfig(shouldUseFreemiumChat),
        staleTime: QUERY_CONSTANTS.CONVERSATIONS_STALE_TIME_MS,
        gcTime: QUERY_CONSTANTS.CONVERSATIONS_GC_TIME_MS,
    });

    // ── Dispatch convenience wrappers ───────────────────────────────────

    const updateMessage = useCallback(
        (messageId: string, updates: Partial<ChatMessage>) =>
            dispatch({ type: DispatchType.UpdateMessage, payload: { messageId, updates } }),
        [dispatch],
    );
    const setMessages = useCallback(
        (msgs: ChatMessage[]) => dispatch({ type: DispatchType.SetMessages, payload: msgs }),
        [dispatch],
    );
    const setConversations = useCallback(
        (convos: StudioAIConversation[]) =>
            dispatch({ type: DispatchType.SetConversations, payload: convos }),
        [dispatch],
    );
    const addConversation = useCallback(
        (c: StudioAIConversation) => dispatch({ type: DispatchType.AddConversation, payload: c }),
        [dispatch],
    );
    const updateConversation = useCallback(
        (conversationId: string, updates: Partial<StudioAIConversation>) =>
            dispatch({
                type: DispatchType.UpdateConversation,
                payload: { conversationId, updates },
            }),
        [dispatch],
    );
    const removeConversation = useCallback(
        (id: string) => dispatch({ type: DispatchType.RemoveConversation, payload: id }),
        [dispatch],
    );
    const setChatState = useCallback(
        (s: ChatState) => dispatch({ type: DispatchType.SetChatState, payload: s }),
        [dispatch],
    );
    const setError = useCallback(
        (e: string | null) => dispatch({ type: DispatchType.SetError, payload: e }),
        [dispatch],
    );
    const setIsLoading = useCallback(
        (l: boolean) => dispatch({ type: DispatchType.SetLoading, payload: l }),
        [dispatch],
    );
    const setCurrentStatus = useCallback(
        (s: string | null) => dispatch({ type: DispatchType.SetCurrentStatus, payload: s }),
        [dispatch],
    );
    const setThreadId = useCallback(
        (id: string | null) => dispatch({ type: DispatchType.SetThreadId, payload: id }),
        [dispatch],
    );
    const setParentMessageId = useCallback(
        (id: string) => dispatch({ type: DispatchType.SetParentMessageId, payload: id }),
        [dispatch],
    );
    const clearError = useCallback(() => dispatch({ type: DispatchType.ClearError }), [dispatch]);
    const reset = useCallback(() => {
        dispatch({ type: DispatchType.Reset });
        setCurrentConversationId(null);
        setIsSharedView(false);
        // Planner / agent / masquerade state lives outside the reducer; clear
        // it here so navigating back to the landing page doesn't leak a stale
        // `plannerRunId` (which would otherwise pin the chat input into the
        // "stop" state) or a stale masquerade/agent mode into the next chat.
        // Only the DANI-only picker's client is cleared: under View-as the
        // client is derived, so leaving a conversation never drops DANI back
        // to MFour (M4S-5670 §4).
        setLocalMasqueradeClient(null);
        setViewAsConversationTag(null);
        setAgentMode(null);
        setPlannerFormState(null);
        setPlannerRunId(null);
        setPlannerPhase(null);
        setPlannerTemplateId(null);
        setPlannerReportData(null);
        setReportPollingConversationId(null);
        setPendingOptions(null);
        setSmartRoutingEnabled(false);
        // Back or the DANI nav link lands here, not in startNewConversation;
        // a queued pre-seed must not ride into the next, unrelated question.
        streaming.duplicateMessagesRef.current = [];
    }, [dispatch, streaming.duplicateMessagesRef]);

    // ── Derived state ───────────────────────────────────────────────────

    // ── URL sync effect ─────────────────────────────────────────────────
    // One-directional: URL change → load conversation + set state.
    // Navigation is handled explicitly by callers (handleSendMessage, openConversation, etc.).

    const { conversationId: urlConversationId, isSharedParam, reportId: urlReportId } = urlSync;

    useEffect(() => {
        if (disableNavigation) return;
        // seedConversation is on its way from the landing page to an unsaved
        // chat: wait for its URL without resetting, and never load it (it would
        // 404). Any other conversation URL cancels the wait and loads normally.
        const pendingSeedId = lifecycle.pendingSeedIdRef.current;
        if (pendingSeedId) {
            if (!urlConversationId) return;
            lifecycle.pendingSeedIdRef.current = null;
            if (urlConversationId === pendingSeedId) return;
        }
        if (lifecycle.isNavigatingToNewChatRef.current) {
            // Only a genuine return to the blank landing — both the URL and
            // the tracked conversation id empty — re-arms this effect. A
            // brand-new conversation's id is set synchronously (before the
            // router.push that aims at it), but the route transition itself
            // is async — a mutation that settles fast (e.g. an immediate
            // 403) can re-render this still-mounted instance with the new
            // currentConversationId while urlConversationId hasn't caught up
            // yet. Checking `!urlConversationId` alone used to misread that
            // split-render as "navigated back to /dani", clearing the flag
            // and letting the block below's `reset()` wipe the very
            // conversation id the push was headed for — which then made the
            // new route's own first render look like a 404 and bounce
            // straight back to the blank landing with no error shown at all.
            if (!urlConversationId && !currentConversationId) {
                lifecycle.isNavigatingToNewChatRef.current = false;
            }
            // Stay blocked while the URL still has a conversation id — otherwise a
            // mid-navigation render can reload the thread we just left (e.g. org switch).
            return;
        }

        if (
            streaming.chatMutation.isPending &&
            // If the URL conversation ID is not equal to the current conversation ID, load conversations.
            // This would mean the user navigated to a conversation URL through browser navigation (e.g. back/forward)
            ((urlConversationId && urlConversationId === currentConversationId) ||
                // Do not load conversations if there's no conversation ID in the URL
                // to avoid attempting to load conversations before the first conversation is created
                !urlConversationId)
        )
            return;

        if (urlConversationId && urlConversationId !== currentConversationId && !urlReportId) {
            if (isSharedParam) setIsSharedView(true);

            // Abort any in-flight SSE stream and reset chatState/isLoading/currentStatus
            // before switching conversations. Without this, navigating away while DANI
            // is responding leaves the loading indicator stuck on the destination conversation.
            streaming.stopConversation();

            lifecycle.loadConversationMessages(urlConversationId, () => {
                dispatch({ type: DispatchType.ClearError });

                if (messagesRef.current.length > 0) {
                    // Messages already in state (e.g. forked conversation not yet persisted) —
                    // stay on the current URL and let the user send the first message.
                    return;
                }

                // Genuine 404 — reset and redirect to /dani.
                setCurrentConversationId(null);
                dispatch({ type: DispatchType.SetMessages, payload: [] });
                setIsSharedView(false);
                lifecycle.isNavigatingToNewChatRef.current = true;
                router.replace('/dani', { scroll: false });
            });

            if (isSharedParam) {
                const basePath = urlReportId
                    ? `/dani/${urlConversationId}/${urlReportId}`
                    : `/dani/${urlConversationId}`;
                router.replace(basePath, { scroll: false });
            }
        }

        if (!urlConversationId && currentConversationId) {
            reset();
        }
    }, [
        disableNavigation,
        urlConversationId,
        urlReportId,
        currentConversationId,
        streaming.chatMutation.isPending,
        streaming.stopConversation,
        lifecycle.loadConversationMessages,
        lifecycle.isNavigatingToNewChatRef,
        lifecycle.pendingSeedIdRef,
        router,
        isSharedParam,
        dispatch,
        reset,
    ]);

    // ── isNavigatingToNewChatRef clean up ─────────────────────────────────────────────────
    // Handle back navigation to reset the isNavigatingToNewChatRef flag to run loadConversationMessages on subsequent URL changes.
    useEffect(() => {
        const handleBack = () => {
            if (lifecycle.isNavigatingToNewChatRef.current) {
                lifecycle.isNavigatingToNewChatRef.current = false;
            }
        };

        window.addEventListener('popstate', handleBack);
        return () => window.removeEventListener('popstate', handleBack);
    }, []);

    // ── Context value ───────────────────────────────────────────────────

    const contextValue = useMemo<StudioAIContextValue>(
        () => ({
            // Conversation tracking
            currentConversationId,

            // Core state
            messages: state.messages,
            chatState: state.chatState,
            error: state.error,
            isLoading: state.isLoading,
            currentStatus: state.currentStatus,
            currentThreadId: state.currentThreadId,
            currentParentMessageId: state.currentParentMessageId,
            planProgress: state.planProgress,
            conversationContextData: state.conversationContextData,
            userSession: session,

            // Conversations
            conversations: Array.isArray(history.conversationsQuery.data?.data)
                ? history.conversationsQuery.data.data
                : [],
            isCurrentConversationOwner,
            conversationsLoading: history.conversationsQuery.isLoading,
            conversationsError: history.conversationsQuery.error,

            // Chat mutation state
            chatLoading: streaming.chatMutation.isPending,
            chatError: streaming.chatMutation.error,
            deleteLoading: history.deleteConversationMutation.isPending,
            deleteError: history.deleteConversationMutation.error,

            // Feedback
            allUserFeedback: Array.isArray(history.allUserFeedbackQuery.data)
                ? history.allUserFeedbackQuery.data
                : [],
            userFeedback: Array.isArray(history.userFeedbackQuery.data)
                ? history.userFeedbackQuery.data
                : [],
            userFeedbackLoading: history.userFeedbackQuery.isLoading,
            userFeedbackError: history.userFeedbackQuery.error,
            userFeedbackFilter: history.userFeedbackFilter,
            setUserFeedbackFilter: history.setUserFeedbackFilter,
            markFeedbackAsRead: history.markFeedbackAsRead,
            refetchUserFeedback: history.refetchUserFeedback,
            unreadFeedbackCount: history.unreadFeedbackCountQuery.data || 0,

            // Data refresh
            dataRefreshDates: history.dataRefreshDatesQuery.data || null,
            dataRefreshDatesLoading: history.dataRefreshDatesQuery.isLoading,
            dataRefreshDatesError: history.dataRefreshDatesQuery.error,

            // Folders
            folders: Array.isArray(history.foldersQuery.data) ? history.foldersQuery.data : [],
            foldersLoading: history.foldersQuery.isLoading,
            foldersError: history.foldersQuery.error,
            folderedConversations: history.folderedConversations,
            folderedReports: history.folderedReports,
            folderConversationTotals: history.folderConversationTotals,
            folderReportTotals: history.folderReportTotals,
            folderConversationParams: history.folderConversationParams,
            folderReportParams: history.folderReportParams,
            setFolderConversationPage: history.setFolderConversationPage,
            setFolderConversationLimit: history.setFolderConversationLimit,
            setFolderReportPage: history.setFolderReportPage,
            setFolderReportLimit: history.setFolderReportLimit,
            conversationsIsFetching: history.conversationsIsFetching,
            reportsIsFetching: history.reportsIsFetching,
            folderConversationFetching: history.folderConversationFetching,
            folderReportFetching: history.folderReportFetching,
            createFolder: history.createFolder,
            renameFolder: history.renameFolder,
            deleteFolder: history.deleteFolder,
            moveConversationToFolder: history.moveConversationToFolder,
            refetchFolders: history.refetchFolders,

            // Sharing
            sharedWithMe: history.sharedWithMeQuery.data ?? [],
            sharedWithMeLoading: history.sharedWithMeQuery.isLoading,
            sharedWithMeError: history.sharedWithMeQuery.error,
            organizationUsers: history.orgUsersQuery.data ?? [],
            organizationUsersLoading: history.orgUsersQuery.isLoading,
            shareConversation: history.shareConversation,
            getConversationShares: history.getConversationShares,
            revokeShare: history.revokeShare,
            refetchSharedWithMe: history.refetchSharedWithMe,
            isSharedView,

            // Reports
            reports: Array.isArray(history.reportsQuery.data?.data)
                ? history.reportsQuery.data.data
                : [],
            reportsLoading: history.reportsQuery.isLoading,

            // Report search / pagination
            searchReports: history.searchReports,
            reportSearchTerm: history.reportSearchTerm,
            reportPage: history.reportPage,
            reportLimit: history.reportLimit,
            totalUnfiledReports: history.totalUnfiledReports,
            reportTotalPages: history.reportTotalPages,
            setReportPage: history.setReportPage,
            setReportLimit: history.setReportLimit,

            // Config
            cautionToolNames: activeConfigQuery.data?.infoCautionToolNames ?? [],

            // Masquerade. The setter is the DANI-only picker's; under View-as
            // the exposed client is derived and the setter cannot change it.
            masqueradeClient,
            setMasqueradeClient: setLocalMasqueradeClient,
            isMasqueradeLocked,
            isGlobalViewAsActive,

            // Agent mode & planner
            agentMode,
            effectiveAgentMode,
            setAgentMode,
            plannerFormState,
            plannerRunId,
            setPlannerRunId,
            plannerPhase,
            setPlannerPhase,
            plannerTemplateId,
            setPlannerTemplateId,
            plannerReportData,
            setPlannerReportData,
            handleReportKickedOff: reportKickoff.handleReportKickedOff,
            startReportPolling,
            smartRoutingEnabled,
            setSmartRoutingEnabled,

            // Freemium
            freemiumLimitReached,
            setFreemiumLimitReached,
            freemiumLimitData,

            // Credit gate
            creditGateData,
            clearCreditGate,

            // Agent options
            pendingOptions,
            setPendingOptions,

            // State setters
            setCurrentConversationId,
            addMessage,
            updateMessage,
            setMessages,
            setConversations,
            addConversation,
            updateConversation,
            removeConversation,
            setChatState,
            setError,
            setIsLoading,
            setCurrentStatus,
            setThreadId,
            setParentMessageId,
            clearError,
            reset,

            // Chat actions
            sendMessage: streaming.sendMessage,
            sendSilentMessage: streaming.sendSilentMessage,
            stopConversation: streaming.stopConversation,

            // Conversation actions
            openConversation: lifecycle.openConversation,
            startNewConversation,
            markNavigatingToNewChat,
            duplicateConversation: lifecycle.duplicateConversation,
            forkConversationFromMessage: lifecycle.forkConversationFromMessage,
            seedConversation,
            seededChat,
            loadConversationMessages: lifecycle.loadConversationMessages,

            // History actions
            deleteConversation: history.deleteConversation,
            renameConversation: history.renameConversation,
            submitFeedback: history.submitFeedback,
            refetchConversations: history.refetchConversations,

            // Search / pagination
            searchConversations: history.searchConversations,
            conversationSearchTerm: history.conversationSearchTerm,
            conversationPage: history.conversationPage,
            conversationLimit: history.conversationLimit,
            totalUnfiledConversations: history.totalUnfiledConversations,
            totalPages: history.totalPages,
            setConversationPage: history.setConversationPage,
            setConversationLimit: history.setConversationLimit,
            setHistoryQueriesEnabled: history.setHistoryQueriesEnabled,

            navigationDisabled: disableNavigation,
        }),
        [
            currentConversationId,
            // Load-bearing for the Share affordances: without it the context
            // value would not recompute when ownership resolves, and consumers
            // would keep reading a stale `false`. See BT-2010.
            isCurrentConversationOwner,
            state,
            session,
            history,
            streaming,
            lifecycle,
            startNewConversation,
            seedConversation,
            seededChat,
            markNavigatingToNewChat,
            activeConfigQuery.data,
            isSharedView,
            masqueradeClient,
            isMasqueradeLocked,
            isGlobalViewAsActive,
            agentMode,
            effectiveAgentMode,
            plannerFormState,
            plannerRunId,
            plannerPhase,
            plannerTemplateId,
            plannerReportData,
            setPlannerReportData,
            reportKickoff.handleReportKickedOff,
            startReportPolling,
            smartRoutingEnabled,
            setSmartRoutingEnabled,
            freemiumLimitReached,
            freemiumLimitData,
            creditGateData,
            clearCreditGate,
            pendingOptions,
            setCurrentConversationId,
            addMessage,
            updateMessage,
            setMessages,
            setConversations,
            addConversation,
            updateConversation,
            removeConversation,
            setChatState,
            setError,
            setIsLoading,
            setCurrentStatus,
            setThreadId,
            setParentMessageId,
            clearError,
            reset,
            disableNavigation,
        ],
    );

    // A reopened plan-mode conversation under View-as while reports as a
    // client are off is locked, so its follow-ups never go silently to /chat.
    const isPlannerLocked = isPlannerLockedForClient({
        isPlanner: agentMode === AgentModes.PLANNER,
        isGlobalViewAsActive,
        plannerAllowedForClient,
    });
    const inputLockedReason = viewAsConversationMismatch
        ? mismatchLockedReason(viewAsConversationMismatch)
        : isPlannerLocked
          ? VIEW_AS_REPORTS_UNAVAILABLE
          : null;
    const viewAsValue = useMemo<StudioAIViewAsValue>(
        () => ({
            conversationMismatch: viewAsConversationMismatch,
            inputLockedReason,
            plannerAllowedForClient,
            hasViewAsControl,
        }),
        [viewAsConversationMismatch, inputLockedReason, plannerAllowedForClient, hasViewAsControl],
    );

    return (
        <StudioAIContext.Provider value={contextValue}>
            <StudioAIViewAsContext.Provider value={viewAsValue}>
                {!disableNavigation && <URLSyncManager onChange={setUrlSync} />}
                {children}
            </StudioAIViewAsContext.Provider>
        </StudioAIContext.Provider>
    );
}

function URLSyncManager({ onChange }: { onChange: (state: URLSyncState) => void }) {
    const params = useParams<{ conversationId?: string; reportId?: string }>();
    const searchParams = useSearchParams();

    const conversationId = params?.conversationId as string | undefined;
    const reportId = params?.reportId as string | undefined;
    const isSharedParam = searchParams?.get('shared') === 'true';

    useEffect(() => {
        onChange({ conversationId, isSharedParam, reportId });
    }, [conversationId, isSharedParam, reportId, onChange]);

    return null;
}

export function useStudioAI(): StudioAIContextValue {
    const context = useContext(StudioAIContext);
    if (context === undefined) {
        throw new Error('useStudioAI must be used within a StudioAIProvider');
    }
    return context;
}
